/**
 * Documents Page
 * Refactored main component - uses sub-components from ./documents/
 * Features: Category grouping, filtering, and color-coded badges
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { 
  FolderOpen, 
  Upload, 
  Loader2,
  LayoutGrid,
  List,
  Archive,
  Layers,
  FileText,
  X,
  Download,
  ExternalLink,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import { useTaxYear } from '../context/TaxYearContext';
import { useSubscription } from '../context/SubscriptionContext';

import { 
  DocumentCard, 
  DocumentRow, 
  UploadDialog, 
  EditDocumentDialog,
  LinkTripDialog,
  CategoryFilter,
  CategorySection,
  getCategoryForType,
  groupDocumentsByCategory,
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPE_LABELS
} from './documents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAX_DOC_SECTIONS = [
  { type: 'p45', label: 'P45', description: 'End of employment certificates' },
  { type: 'p60', label: 'P60', description: 'End of year tax summaries' },
  { type: 'p11d', label: 'P11D', description: 'Benefits and expenses declarations' },
];

const TaxDocumentsSectioned = ({ documents, onEdit, onDelete, onPreview, onLink, onSavePassword }) => {
  const grouped = {};
  TAX_DOC_SECTIONS.forEach(s => { grouped[s.type] = []; });
  documents.forEach(doc => {
    if (grouped[doc.document_type]) {
      grouped[doc.document_type].push(doc);
    }
  });

  return (
    <div className="space-y-5" data-testid="tax-docs-sectioned">
      {TAX_DOC_SECTIONS.map(section => (
        <Card key={section.type} className="bg-slate-800 border-slate-700" data-testid={`section-${section.type}`}>
          <div className="flex items-center gap-3 px-4 pt-4 pb-2 border-b border-slate-700">
            <div className="p-1.5 rounded-md bg-purple-500/10">
              <FileText className="h-4 w-4 text-purple-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">{section.label}</h3>
              <p className="text-slate-500 text-xs">{section.description}</p>
            </div>
            <span className="ml-auto text-xs text-slate-500">
              {grouped[section.type].length} document{grouped[section.type].length !== 1 ? 's' : ''}
            </span>
          </div>
          <CardContent className="p-3">
            {grouped[section.type].length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No {section.label} documents uploaded</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {grouped[section.type].map(doc => (
                  <DocumentCard key={doc.id} doc={doc} onEdit={onEdit} onDelete={onDelete} onPreview={onPreview} onLink={onLink} onSavePassword={onSavePassword} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const SavePasswordForm = ({ doc, onSaved, onCancel }) => {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim() || !doc) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/documents/${doc.id}/password`, { password });
      toast.success('Password saved');
      onSaved();
    } catch {
      toast.error('Failed to save password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-2">
      <div className="p-3 bg-slate-700/50 rounded-lg">
        <p className="text-sm text-slate-300 truncate">{doc?.file_name}</p>
      </div>
      <div className="relative">
        <Input
          type={show ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter PDF password"
          className="bg-slate-700/50 border-slate-600 text-white pr-10"
          autoFocus
          data-testid="doc-save-password-input"
        />
        <button type="button" onClick={() => setShow(!show)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <p className="text-[11px] text-slate-500">
        Your accountant will also be able to view this document without needing the password.
      </p>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}
          className="border-slate-600 text-slate-300 hover:bg-slate-700" disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={!password.trim() || saving}
          className="bg-amber-600 hover:bg-amber-700" data-testid="doc-save-password-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
          Save Password
        </Button>
      </div>
    </form>
  );
};

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [taxYears, setTaxYears] = useState([]);
  const { selectedYear, setSelectedYear } = useTaxYear();
  const { canAddContent } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grouped'); // 'grid', 'list', 'grouped'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [linkingDoc, setLinkingDoc] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [savePasswordDoc, setSavePasswordDoc] = useState(null);

  // Calculate category counts
  const categoryCounts = useMemo(() => {
    const counts = {};
    Object.keys(DOCUMENT_CATEGORIES).forEach(key => {
      counts[key] = 0;
    });
    documents.forEach(doc => {
      const category = getCategoryForType(doc.document_type);
      counts[category.key] = (counts[category.key] || 0) + 1;
    });
    return counts;
  }, [documents]);

  // Filter and group documents
  const filteredDocuments = useMemo(() => {
    if (!selectedCategory) return documents;
    return documents.filter(doc => {
      const category = getCategoryForType(doc.document_type);
      return category.key === selectedCategory;
    });
  }, [documents, selectedCategory]);

  const groupedDocuments = useMemo(() => {
    return groupDocumentsByCategory(filteredDocuments);
  }, [filteredDocuments]);

  const fetchTaxYears = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/tax-years`);
      setTaxYears(response.data.tax_years);
      if (response.data.tax_years.length > 0 && !selectedYear) {
        setSelectedYear(response.data.tax_years[0]);
      }
    } catch (error) {
    }
  }, [selectedYear, setSelectedYear]);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const url = selectedYear && selectedYear !== 'all'
        ? `${API_URL}/api/documents?tax_year=${selectedYear}`
        : `${API_URL}/api/documents`;
      const response = await axios.get(url);
      setDocuments(response.data.documents);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchTaxYears();
  }, [fetchTaxYears]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setEditDialogOpen(true);
  };

  const handlePreview = (doc) => {
    const url = `${API_URL}/api/documents/${doc.id}/preview`;
    axios.get(url, { responseType: 'blob' })
      .then(res => {
        const blobUrl = URL.createObjectURL(res.data);
        setPreviewUrl(blobUrl);
        setPreviewDoc(doc);
      })
      .catch(() => {
        toast.error('Failed to load document preview');
      });
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    setEditingDoc(null);
    fetchDocuments();
  };

  const handleUploadSuccess = () => {
    setDialogOpen(false);
    fetchDocuments();
  };

  const handleLink = (doc) => {
    setLinkingDoc(doc);
    setLinkDialogOpen(true);
  };

  const handleLinkSuccess = () => {
    setLinkDialogOpen(false);
    setLinkingDoc(null);
    fetchDocuments();
  };

  const handleExportZip = async () => {
    setExporting(true);
    try {
      const url = selectedYear && selectedYear !== 'all'
        ? `${API_URL}/api/documents/export/zip?tax_year=${selectedYear}`
        : `${API_URL}/api/documents/export/zip`;
      
      const response = await axios.get(url);
      
      const zipContent = atob(response.data.zip_content);
      const bytes = new Uint8Array(zipContent.length);
      for (let i = 0; i < zipContent.length; i++) {
        bytes[i] = zipContent.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: 'application/zip' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Exported ${response.data.document_count} documents`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No documents found to export');
      } else {
        toast.error('Failed to export documents');
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Documents</h1>
          <p className="text-slate-400 mt-1">Upload and manage supporting documents for your tax claims</p>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* View Mode Toggle */}
          <div className="flex border border-slate-600 rounded-lg overflow-hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('grouped')}
              className={`rounded-none h-9 px-3 ${viewMode === 'grouped' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}
              data-testid="grouped-view-btn"
              title="Grouped by category"
            >
              <Layers className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('grid')}
              className={`rounded-none h-9 px-3 ${viewMode === 'grid' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}
              data-testid="grid-view-btn"
              title="Grid view"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('list')}
              className={`rounded-none h-9 px-3 ${viewMode === 'list' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}
              data-testid="list-view-btn"
              title="List view"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>

          {/* Export ZIP Button */}
          <Button 
            variant="outline" 
            className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            onClick={handleExportZip}
            disabled={exporting || documents.length === 0}
            data-testid="export-zip-btn"
          >
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Archive className="h-4 w-4 mr-2" />}
            {exporting ? 'Exporting...' : 'Download All'}
          </Button>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50" data-testid="upload-document-btn" disabled={!canAddContent}>
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
            </DialogTrigger>
            <UploadDialog 
              taxYears={taxYears} 
              onSuccess={handleUploadSuccess}
              onClose={() => setDialogOpen(false)}
            />
          </Dialog>
        </div>
      </div>

      {/* Category Filter */}
      {documents.length > 0 && (
        <CategoryFilter 
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          categoryCounts={categoryCounts}
        />
      )}

      {/* Documents Display */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
        </div>
      ) : documents.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <FolderOpen className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-300 mb-2">No documents yet</h3>
            <p className="text-slate-500 mb-4">Upload wage slips, P60s, flight stubs and other supporting documents.</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-amber-600 hover:bg-amber-700">
              <Upload className="h-4 w-4 mr-2" />
              Upload Your First Document
            </Button>
          </CardContent>
        </Card>
      ) : selectedCategory === 'tax_documents' ? (
        /* P45/P60/P11D Sectioned View */
        <TaxDocumentsSectioned documents={filteredDocuments} onEdit={handleEdit} onDelete={handleDelete} onPreview={handlePreview} onLink={handleLink} onSavePassword={(doc) => setSavePasswordDoc(doc)} />
      ) : viewMode === 'grouped' ? (
        /* Grouped View - Documents organized by category */
        <div className="space-y-4">
          {groupedDocuments.map((category) => (
            <CategorySection
              key={category.key}
              category={category}
              viewMode="grid"
              onEdit={handleEdit}
              onDelete={handleDelete}
              onPreview={handlePreview}
              onLink={handleLink}
              onSavePassword={(doc) => setSavePasswordDoc(doc)}
            />
          ))}
        </div>
      ) : viewMode === 'grid' ? (
        /* Grid View - Flat grid of document cards */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocuments.map((doc) => (
            <DocumentCard key={doc.id} doc={doc} onEdit={handleEdit} onDelete={handleDelete} onPreview={handlePreview} onLink={handleLink} onSavePassword={(doc) => setSavePasswordDoc(doc)} />
          ))}
        </div>
      ) : (
        /* List View - Table-style list */
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-0">
            <div className="divide-y divide-slate-700">
              <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-900/50 text-xs font-medium text-slate-400 uppercase tracking-wider">
                <div className="col-span-4">File Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Tax Year</div>
                <div className="col-span-2">Uploaded</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {filteredDocuments.map((doc) => (
                <DocumentRow key={doc.id} doc={doc} onEdit={handleEdit} onDelete={handleDelete} onPreview={handlePreview} onLink={handleLink} onSavePassword={(doc) => setSavePasswordDoc(doc)} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Document Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) setEditingDoc(null);
      }}>
        <EditDocumentDialog 
          document={editingDoc}
          taxYears={taxYears}
          onSuccess={handleEditSuccess}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingDoc(null);
          }}
        />
      </Dialog>

      {/* Link to Trip Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={(open) => {
        setLinkDialogOpen(open);
        if (!open) setLinkingDoc(null);
      }}>
        {linkingDoc && (
          <LinkTripDialog
            document={linkingDoc}
            onSuccess={handleLinkSuccess}
            onClose={() => { setLinkDialogOpen(false); setLinkingDoc(null); }}
          />
        )}
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent className="bg-slate-800 border-slate-700 w-[95vw] max-w-5xl h-[85vh] flex flex-col p-0" data-testid="document-preview-modal">
          <DialogHeader className="px-4 py-3 border-b border-slate-700 flex-shrink-0 pr-14">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-white text-sm truncate pr-4">
                {previewDoc?.file_name}
              </DialogTitle>
              <div className="flex items-center gap-2 flex-shrink-0">
                {previewUrl && (
                  <a
                    href={previewUrl}
                    download={previewDoc?.file_name}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
                    data-testid="preview-download-btn"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </a>
                )}
                {previewUrl && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
                    data-testid="preview-newtab-btn"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    New Tab
                  </a>
                )}
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-slate-900">
            {previewUrl ? (
              previewDoc?.file_name?.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-full border-0"
                  title="Document Preview"
                  data-testid="preview-iframe"
                />
              ) : previewDoc?.file_name?.toLowerCase().match(/\.(png|jpg|jpeg|gif|webp)$/) ? (
                <div className="w-full h-full flex items-center justify-center p-4">
                  <img
                    src={previewUrl}
                    alt={previewDoc?.file_name}
                    className="max-w-full max-h-full object-contain rounded"
                    data-testid="preview-image"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <FileText className="h-16 w-16 text-slate-600" />
                  <p className="text-slate-400">Preview not available for this file type</p>
                  <a
                    href={previewUrl}
                    download={previewDoc?.file_name}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Download File
                  </a>
                </div>
              )
            ) : (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Password Dialog */}
      <Dialog open={!!savePasswordDoc} onOpenChange={(open) => { if (!open) setSavePasswordDoc(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-400" />
              Save PDF Password
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Save the password so you and your accountant can open this document without re-entering it.
            </DialogDescription>
          </DialogHeader>
          <SavePasswordForm
            doc={savePasswordDoc}
            onSaved={() => { setSavePasswordDoc(null); fetchDocuments(); }}
            onCancel={() => setSavePasswordDoc(null)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
